'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

export type TemplateStore = { name: string; slug: string; categories: string[] };

function TemplateCard({ store }: { store: { name: string; slug: string } }) {
  return (
    <Link
      href={`/${store.slug}`}
      className="flex-shrink-0 group cursor-pointer template-card"
      style={{ width: '225px', textDecoration: 'none' }}
    >
      <div
        className="template-card-inner p-4 overflow-hidden"
        style={{
          backgroundColor: '#E7F5FF', border: '2px solid transparent',
          borderRadius: '8px', maxHeight: '364px',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div className="flex flex-col items-center flex-1" style={{ overflow: 'hidden', position: 'relative' }}>
          <div className="flex items-start justify-center flex-1" style={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
            <Image
              src={`/receipts/${store.slug.replace('-receipt', '')}-receipt-template.png`}
              alt={`Preview of ${store.name} receipt template`}
              width={280}
              height={600}
              sizes="200px"
              className="rounded"
              style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', objectPosition: 'top center', borderRadius: '0' }}
            />
            <div
              className="receipt-gradient absolute bottom-0 left-0 right-0 transition-all duration-0"
              style={{ height: '20%', background: 'linear-gradient(to top, #E7F5FF, transparent)', pointerEvents: 'none' }}
            />
          </div>
          <h3
            className="store-title text-center font-medium mt-4"
            style={{ fontFamily: 'Roboto', fontSize: '18px', color: '#1a1a2e', transition: 'color 0.2s' }}
          >
            {store.name} Receipt
          </h3>
        </div>
      </div>
    </Link>
  );
}

export default function TemplateCarouselClient({
  stores,
  categories,
  featuredNames,
}: {
  stores: TemplateStore[];
  categories: string[];
  featuredNames: string[];
}) {
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [currentDotIndex, setCurrentDotIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const templateStores = (selectedCategory === 'All Categories'
    ? stores.filter(store => featuredNames.includes(store.name))
    : stores.filter(store => store.categories.includes(selectedCategory))
  ).slice(0, 5);

  const totalDots = Math.ceil(templateStores.length / 2);

  const scrollToDot = (index: number) => {
    if (scrollContainerRef.current) {
      const cardWidth = 225 + 24;
      const scrollPosition = index * cardWidth * 2;
      scrollContainerRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      setCurrentDotIndex(index);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let rafId = 0;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const scrollLeft = container.scrollLeft;
        const cardWidth = 225 + 24;
        const newIndex = Math.round(scrollLeft / (cardWidth * 2));
        setCurrentDotIndex(Math.min(newIndex, totalDots - 1));
        rafId = 0;
      });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [totalDots]);

  return (
    <>
      {/* Category Dropdown — native select to avoid Radix Select bundle cost */}
      <div className="flex justify-center mb-8">
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="appearance-none cursor-pointer focus:outline-none focus:border-[#0068D6]"
            style={{
              fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px',
              height: '40px', minWidth: '200px',
              border: '1px solid #0285FF', borderRadius: '4px',
              backgroundColor: '#FFFFFF', color: '#1a1a2e',
              paddingLeft: '12px', paddingRight: '36px',
            }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0285FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </div>
      </div>

      {/* Filtered template cards */}
      <div>
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto pb-4 hide-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', paddingLeft: '8px', paddingRight: '8px' }}
        >
          <div className="flex gap-6 template-cards-row" style={{ minWidth: 'max-content' }}>
            {templateStores.map((store, index) => (
              <TemplateCard key={index} store={store} />
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-0 mt-4 template-pagination-dots lg:hidden">
          {Array.from({ length: totalDots }).map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToDot(index)}
              className="template-dot"
              style={{
                width: '48px', height: '48px', borderRadius: '50%', border: 'none',
                backgroundColor: 'transparent',
                backgroundImage: `radial-gradient(circle, ${currentDotIndex === index ? '#0068D6' : '#D0D0D0'} 4px, transparent 4px)`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                cursor: 'pointer', transition: 'background-image 0.2s',
                padding: 0,
              }}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}
